import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listUsers, createUser, updateUser, deleteUser } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Pencil, Trash2, Shield, Eye, Lock } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { admin: "Administrator", readonly: "Read Only" };
const emptyForm = { username: "", name: "", email: "", role: "readonly", password: "" };

export default function Users() {
  const qc = useQueryClient();
  const { isAdmin, user } = useAuth();
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: listUsers, enabled: isAdmin });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // username being edited, or null for create
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="kcard p-10 text-center border-2 border-dashed border-[var(--line)]">
          <Lock className="w-9 h-9 mx-auto text-slate-300" />
          <div className="text-sm font-medium text-slate-600 mt-3">Administrator access required</div>
          <div className="text-[12px] text-slate-400 mt-1">User management is only available to admin accounts.</div>
        </div>
      </div>
    );
  }

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (u) => { setEditing(u.username); setForm({ username: u.username, name: u.name || "", email: u.email || "", role: u.role, password: "" }); setOpen(true); };

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) {
        await updateUser(editing, { name: form.name, email: form.email, role: form.role, ...(form.password ? { password: form.password } : {}) });
        toast.success(`User '${editing}' updated`);
      } else {
        await createUser(form);
        toast.success(`User '${form.username}' created`);
      }
      qc.invalidateQueries({ queryKey: ["users"] });
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save user");
    } finally { setBusy(false); }
  };

  const doDelete = async (u) => {
    try {
      await deleteUser(u.username);
      toast.success(`User '${u.username}' deleted`);
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete user");
    } finally { setConfirmDel(null); }
  };

  return (
    <div className="p-6 space-y-5 max-w-4xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-eyebrow">Administration</div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">User Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">Create and manage accounts and their access roles. Changes take effect immediately.</p>
        </div>
        <button onClick={openCreate} data-testid="add-user-btn" className="h-10 px-4 rounded-md text-white text-sm font-medium inline-flex items-center gap-2 shrink-0" style={{ background: "var(--kcsa-red)" }}>
          <UserPlus className="w-4 h-4" /> Add User
        </button>
      </div>

      <div className="kcard p-0 overflow-hidden">
        <table className="w-full text-[13px]" data-testid="users-table">
          <thead className="bg-[var(--surface)]"><tr>{["Username", "Name", "Email", "Role", ""].map((h, i) => <th key={i} className="thead-cell px-4 py-2.5 text-left">{h}</th>)}</tr></thead>
          <tbody>
            {(users || []).map((u) => (
              <tr key={u.username} className="border-t border-[var(--line)]" data-testid={`user-row-${u.username}`}>
                <td className="px-4 py-2.5 font-medium text-slate-800">{u.username}{u.username.toLowerCase() === user.username.toLowerCase() && <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">You</span>}</td>
                <td className="px-4 py-2.5 text-slate-600">{u.name || "—"}</td>
                <td className="px-4 py-2.5 text-slate-600">{u.email || "—"}</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${u.role === "admin" ? "bg-red-50 text-[var(--kcsa-red-dark)]" : "bg-slate-100 text-slate-600"}`}>
                    {u.role === "admin" ? <Shield className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {ROLE_LABEL[u.role]}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.username}`} title="Edit" className="w-8 h-8 grid place-items-center rounded-md hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => setConfirmDel(u)} data-testid={`delete-user-${u.username}`} title="Delete" className="w-8 h-8 grid place-items-center rounded-md hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {(!users || users.length === 0) && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No users yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing}` : "Add New User"}</DialogTitle>
            <DialogDescription>{editing ? "Update this account's details, role or password." : "Create a new account and assign its access role."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {!editing && (
              <Field label="Username">
                <input value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} data-testid="user-username" className="field-input" placeholder="e.g. jdoe" />
              </Field>
            )}
            <Field label="Full Name">
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="user-name" className="field-input" placeholder="Jane Doe" />
            </Field>
            <Field label="Email">
              <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="user-email" className="field-input" placeholder="jane@company.com" />
            </Field>
            <Field label="Role">
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger className="h-9 text-[13px]" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="readonly">Read Only</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={editing ? "Reset Password (optional)" : "Password"}>
              <input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="user-password" className="field-input" placeholder={editing ? "Leave blank to keep current" : "Min 6 characters"} />
            </Field>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="h-9 px-4 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="save-user-btn" className="h-9 px-5 rounded-md text-white text-sm font-medium disabled:opacity-50" style={{ background: "var(--kcsa-red)" }}>{busy ? "Saving…" : editing ? "Save Changes" : "Create User"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle>Delete user</DialogTitle>
            <DialogDescription>This permanently removes the account.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">Are you sure you want to delete <b>{confirmDel?.username}</b>? This cannot be undone.</p>
          <DialogFooter>
            <button onClick={() => setConfirmDel(null)} className="h-9 px-4 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
            <button onClick={() => doDelete(confirmDel)} data-testid="confirm-delete-user-btn" className="h-9 px-5 rounded-md text-white text-sm font-medium" style={{ background: "var(--kcsa-red)" }}>Delete</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`.field-input{width:100%;height:36px;border-radius:6px;border:1px solid var(--line);padding:0 12px;font-size:13px;outline:none}.field-input:focus{border-color:#94a3b8}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><div className="label-eyebrow mb-1">{label}</div>{children}</div>
);
